import React from 'react';
import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { DatePicker } from 'ui-components/components/date-picker';
import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Input } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './purchase-order-details-messages';
import styles from './purchase-order-details.styles.module.less';
import { useScreenSizeContext } from '../../../../../contexts/screen-size-resolution';
import Global from '../../../../../store/global-selectors';
import { purchaseOrderProperties } from '../../data/purchase-order-properties';
import { usePurchaseOrderContext } from '../../purchase-order-context';
import { PurchaseOrderSectionId } from '../../purchase-order-expandable-section-provider';
import { ExpandableSection } from '../expandable-section';

const shippingDetailsMessages = defineMessages({
  shippingDetailsSection: {
    defaultMessage: 'Shipping Details Section',
    id: 'O2/7me',
    description: 'Aria label for shipping details section',
  },
});

/**
 * @module PurchaseOrderShippingDetails
 * @description A component that renders the shipping details section of a purchase order.
 * This component uses the PurchaseOrderContext to access and update the purchase order state.
 * The shipping details section includes:
 * - Shipping carrier and tracking number
 * - Reference number
 * - Order ID, order date, and expected delivery date
 * @returns {JSX.Element} A form section with shipping details fields
 * @example
 * <PurchaseOrderShippingDetails />
 */
export const PurchaseOrderShippingDetails = () => {
  const { purchaseOrder, setPurchaseOrder, validateForm, setFieldTouched, carriersList } = usePurchaseOrderContext();
  const dateFormat = useSelector(Global.selectUserUserDateFormat);
  const { isTablet, isMobile, isUltraMobile } = useScreenSizeContext();
  const isSmallScreen = isTablet || isMobile || isUltraMobile;

  const handleInputChange = (field, value) => {
    setPurchaseOrder((order) => {
      const updatedOrder = {
        ...order,
        [field]: value,
      };

      setFieldTouched(field);
      validateForm(updatedOrder);
      return updatedOrder;
    });
  };

  const expectedDeliveryDateColumn = (
    <FlexColumn className={styles['column-3']}>
      <Text className={styles['label-text']} textType={TextType.Label} value={messages.expectedDeliveryDate} />
      <DatePicker
        allowPastDates
        calendarPosition='bottom-right'
        className={styles['date-picker']}
        dateFormat={dateFormat}
        onChange={(value) => {
          handleInputChange(purchaseOrderProperties.expectedDeliveryDate, value);
        }}
        placeholder=''
        shouldInputHaveFullWidth
        value={purchaseOrder.expectedDeliveryDate || ''}
      />
    </FlexColumn>
  );

  // Content to be displayed when the section is expanded
  const shippingDetailsContent = (
    <>
      <FlexRow className={styles['edit-row']}>
        <FlexColumn className={styles.column}>
          <Text className={styles['label-text']} textType={TextType.Label} value={messages.shippingCarrier} />
          <SearchableDropdownField
            onItemSelected={(value) => handleInputChange(purchaseOrderProperties.carrierId, value)}
            options={carriersList}
            selectedItemValue={purchaseOrder.carrierId}
          />
        </FlexColumn>
        <FlexColumn className={styles.column}>
          <Text className={styles['label-text']} textType={TextType.Label} value={messages.trackingNumber} />
          <Input
            onChange={(value) => handleInputChange(purchaseOrderProperties.trackingNumber, value)}
            value={purchaseOrder.trackingNumber}
          />
        </FlexColumn>
      </FlexRow>
      <FlexRow className={styles['edit-row']}>
        <FlexColumn className={styles.column}>
          <Text className={styles['label-text']} textType={TextType.Label} value={messages.referenceNumber} />
          <Input
            onChange={(value) => handleInputChange(purchaseOrderProperties.referenceNumber, value)}
            value={purchaseOrder.referenceNumber}
          />
        </FlexColumn>
        <FlexColumn className={styles.column} />
      </FlexRow>
      <FlexRow className={styles['edit-row']}>
        <FlexColumn className={styles.column}>
          <FlexRow>
            <FlexColumn className={styles['column-3']}>
              <Text className={styles['label-text']} textType={TextType.Label} value={messages.orderIdLabel} />
              <Input disabled value={purchaseOrder.orderNumber} />
            </FlexColumn>
            <FlexColumn className={styles['column-3']}>
              <Text className={styles['label-text']} textType={TextType.Label} value={messages.orderDate} />
              <DatePicker
                allowPastDates
                calendarPosition='bottom-right'
                className={styles['date-picker']}
                dateFormat={dateFormat}
                onChange={(value) => {
                  handleInputChange(purchaseOrderProperties.orderDate, value);
                }}
                placeholder=''
                shouldInputHaveFullWidth
                value={purchaseOrder.orderDate || ''}
              />
            </FlexColumn>
            {!isSmallScreen ? expectedDeliveryDateColumn : null}
          </FlexRow>
          {isSmallScreen ? <FlexRow className={styles['edit-row']}>{expectedDeliveryDateColumn}</FlexRow> : null}
        </FlexColumn>
        <FlexColumn className={styles.column} />
      </FlexRow>
    </>
  );

  // Header content for the expandable section
  const headerContent = <Text textType={TextType.H4} value={messages.shippingDetails} />;

  return (
    <ExpandableSection
      ariaLabelMessageDescriptor={shippingDetailsMessages.shippingDetailsSection}
      className={styles['shipping-details-wrapper']}
      contentClassName={styles['expandable-content-container']}
      headerClassName={styles['expandable-header-container']}
      headerContent={headerContent}
      sectionContent={shippingDetailsContent}
      sectionId={PurchaseOrderSectionId.ShippingDetails}
    />
  );
};
